import { Box, Icon, Spinner, Table } from '@amzn/awsui-components-core';
import {
  ContentType,
  PatternAccreditation,
  PatternProvenance,
  PatternType,
  PublicationSearchResultItem,
  PublicationType,
  TechDomain,
} from '@amzn/awscat-pattern-library-service-model';
import { DateTime } from 'luxon';
import React from 'react';
import * as UtilApi from '../../api/UtilsApi';
import constants from '../../common/constants';
import PublicationLink from '../publication/PublicationLink';

const duplicatesColumnDefinitions = [
  {
    cell: (item: PublicationSearchResultItem) => (
      <PublicationLink
        contentName={item.contentName}
        contentId={item.contentId}
        workflowId={item.publicationWorkflowId}
        target="_blank"
      />
    ),
    header: 'Title',
    id: 'title',
  },
  {
    cell: (item: PublicationSearchResultItem) => (
      <Box variant="p" color="text-body-secondary">{`${item.contentType || '-'}`}</Box>
    ),
    header: 'Content type',
    id: 'contentType',
  },
  {
    cell: (item: PublicationSearchResultItem) => (
      <Box
        variant="p"
        color="text-body-secondary"
      >{`${DateTime.fromISO(item.publishDate).toLocaleString(DateTime.DATE_FULL) || '-'}`}</Box>
    ),
    header: 'Last update',
    id: 'lastUpdate',
  },
];

interface Props {
  contentName: string;
  isSearchingDuplicates: boolean;
  duplicateContents: PublicationSearchResultItem[];
  numberOfDuplicatesToShow: number;
  loadingMessage?: string;
  noDuplicatesMessage?: string;
  matchFoundMessage?: (count: number) => string;
  duplicateContentMessage?: string;
  compactLayout?: boolean;
  handleShowMoreDuplicatesClick: () => void;
}

const DuplicationCheck: React.FunctionComponent<Props> = (props: Props) => {
  const {
    contentName,
    isSearchingDuplicates,
    duplicateContents,
    numberOfDuplicatesToShow,
    loadingMessage,
    noDuplicatesMessage,
    matchFoundMessage,
    duplicateContentMessage,
    compactLayout,
    handleShowMoreDuplicatesClick,
  } = props;

  if (!contentName) {
    return null;
  }

  const duplicatesTotalCount = duplicateContents.length;
  const duplicatesToShow = duplicateContents.slice(0, numberOfDuplicatesToShow);

  if (isSearchingDuplicates) {
    return (
      <Box variant="div" padding={{ left: 'm', bottom: 's' }}>
        <Spinner />
        <Box
          variant="p"
          padding={{ left: 'xs' }}
          display="inline"
          fontSize="body-s"
          color="text-body-secondary"
        >
          {loadingMessage || constants.CONTENT_CREATION_DUPLICATE_SEARCH_MESSAGE}
        </Box>
      </Box>
    );
  }
  if (!duplicatesTotalCount) {
    return (
      <Box variant="div" padding={{ left: 'm', bottom: 'm' }}>
        <Icon variant="success" name="status-positive" />
        <Box
          variant="p"
          padding={{ left: 'xs' }}
          display="inline"
          fontSize="body-s"
          color="text-body-secondary"
        >
          {noDuplicatesMessage || constants.CONTENT_CREATION_NO_DUPLICATE_CONTENTS}
        </Box>
      </Box>
    );
  }
  const duplicateContentInfo = matchFoundMessage
    ? matchFoundMessage(duplicatesTotalCount)
    : `We found ${duplicatesTotalCount} article${duplicatesTotalCount > 1 ? 's' : ''}` +
      ` that ${duplicatesTotalCount > 1 ? 'are' : 'is'} similar to your title`;

  const numberOfMoreDuplicatesToAdd =
    duplicatesTotalCount - numberOfDuplicatesToShow >= UtilApi.numberOfDuplicatesToAdd
      ? UtilApi.numberOfDuplicatesToAdd
      : duplicatesTotalCount - numberOfDuplicatesToShow;

  const showMoreDuplicatesButton =
    numberOfDuplicatesToShow < duplicatesTotalCount ? (
      <div className="show-more-duplicate" onClick={handleShowMoreDuplicatesClick}>
        <Box variant="div" padding={{ left: 'm', top: 's', bottom: 's' }}>
          <Icon variant="success" name="treeview-expand" />
          <Box
            variant="p"
            padding={{ left: 'xs' }}
            display="inline"
            fontSize="body-s"
            color="text-body-secondary"
          >
            {`Show ${numberOfMoreDuplicatesToAdd} more`}
          </Box>
        </Box>
      </div>
    ) : null;

  const tableContent = (
    <>
      <Table
        loadingText="searching duplicate content"
        columnDefinitions={duplicatesColumnDefinitions}
        className="duplicate-content-table"
        items={duplicatesToShow}
      />
      {showMoreDuplicatesButton}
    </>
  );

  const headerContent = (
    <Box padding={{ left: 'm', top: 'xs' }}>
      <Icon variant="error" name="status-warning" />
      <Box variant="p" display="inline" fontSize="body-s" margin={{ left: 'xs' }}>
        <Box variant="strong">{duplicateContentInfo}</Box>
      </Box>
      <Box variant="p" fontSize="body-s" margin={{ left: 's' }}>
        {duplicateContentMessage || constants.CONTENT_CREATION_DUPLICATE_CONTENT_MESSAGE}
      </Box>
    </Box>
  );

  if (compactLayout) {
    return (
      <div className="duplicate-title">
        {headerContent}
        {tableContent}
      </div>
    );
  }

  return (
    <>
      <div className="duplicate-title">{headerContent}</div>
      <div>{tableContent}</div>
    </>
  );
};

export default DuplicationCheck;
